import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { List } from '@mui/material';
import { GridContainer, CenterContent } from 'global/components/perform-grid';
import { useLocation } from 'react-router-dom';
import NavToTop from 'feature/nav/NavToTop';
import useStyles from './styles';
import { get, set, size, debounce, upperFirst } from 'global/lib/lodash';
import SideBar from './sidebar';
import TaskNotify from '../../task/TaskNotify';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { ScoreLoading } from 'global/components/skeleton-loader';
import TaskBanner from './TaskBanner';
import sendData from 'redux/actions/sendData';
import { useDispatch } from 'react-redux';
import { addNewTask } from 'redux/actions/view';
import { wait } from 'global/utils';
import { useAccessEdition } from 'global/utils/edition';
import { awaitAll } from 'global/utils/awaitAll';
import { useUser } from 'global/hooks/useUser';
import { getItemType, getItemKey, getId, isGob, filterData, setFilters } from './utils';
import { ActiveEdit } from './ActiveEdit';
import { SystemScore } from './SystemScore';
import FilterLogic from './FilterLogic';
import { ObservationCreateFab } from './ObservationCreate';
import { stringObj } from '../../../global/utils';
import AssessmentSnackBar from './AssessmentSnackBar';
import clsx from 'clsx';
import { AUTOTAG } from './ObservationCreate/ScoreTag';
export * from './utils';

const ReportAssessmentScoreContent = (props) => {
  const { assessment, scores, printView, scIndex, canShowScores, isCourse, onToggleEdit, isTemplate } = props;

  //state
  const [selectedItems, setSelectedItems] = useState({});
  const [showSelectBoxes, setShowSelectBoxes] = useState(false);
  const [scorecardIndex, setScorecardIndex] = useState(scIndex !== undefined ? scIndex : 0);
  let [allData, setAllData] = useState(get(scores, 'data', []));
  let [treed, setTreed] = useState([]);
  const [sidebarOpen, setSidebar] = useState(false);
  const [activeEdit, _setActiveEdit] = useState();
  const [searchString, setSearchString] = useState('');
  const [filter, setFilter] = useState({});

  //data
  const scorecards = get(assessment, 'data.scorecards', []);
  const activeSc = get(scorecards, scorecardIndex, {});
  const scid = get(activeSc, 'id');
  let scoredata = get(allData, scid, []);
  const status = get(assessment, 'data.status', 'draft');
  const assessment_id = get(assessment, 'data.id');
  const realm = get(assessment, 'data.realm');
  const realm_id = get(realm, 'id');
  const userCanEdit = (() => {
    if (status === 'published') {
      return false;
    }

    if (props.userCanEdit) {
      return true;
    } else {
      return false;
    }
  })();

  useEffect(() => {
    console.log(get(activeSc, 'config'));
  }, [activeSc]);

  const hasSubScores = !isTemplate && !isCourse && get(activeSc, `config.hasSubScores`, false);

  const realmFilter = useMemo(() => {
    if (!realm || !hasSubScores) {
      return null;
    }

    const options = {
      division: 'community',
      community: 'lot',
      lot: 'room',
    };
    return {
      tier: get(options, realm.tier),
      parent_id: realm.tier == 'lot' ? undefined : realm.id,
    };
  }, [realm, activeSc]);

  const rooms = useRetrieveData(
    { url: '/realms' },
    {
      useEffect: true,
      waitFor: realmFilter,
      build: ['realms'],
      params: {
        filter: realmFilter || {},
      },
    }
  );
  const roomsLoading = get(rooms, 'loading');

  const assessmentLoading = get(assessment, 'loading');
  const loading = assessmentLoading || get(scores, 'loading');
  const error = get(scores, 'error', false);

  //hooks
  const location = useLocation();
  const { me } = useUser();
  const dispatch = useDispatch();
  const userCanLink = useAccessEdition(get(activeSc, `editionId`)) && get(filter, 'listGroup') !== 'location';
  const classes = useStyles();
  const assessmentTasks = useRetrieveData(
    { url: '/tasks' },
    {
      waitFor: assessment_id && realm_id ? undefined : null,
      build: ['tasks'],
      params: {
        filter: {
          assessment: assessment_id,
          realm: realm_id,
        },
        include: 'subScore',
      },
    }
  );
  const setCurrentAnchor = useCallback(
    debounce(
      (toShow, goTo = false) => {
        if (props.isReview) {
          return;
        }
        location.state = { ...location.state, id: getId(toShow) };
      },
      50,
      { leading: false }
    ),
    []
  );
  const setAlerts = useCallback((a) => {
    if (!!a) {
      location.state = { ...location.state, alert: get(a, 'message', ''), severity: get(a, 'severity', 'success') };
    } else {
      location.state = { ...location.state, alert: '', severity: '' };
    }
  }, []);

  //effects
  useEffect(() => {
    setAllData(get(scores, 'data', []));
  }, [scores.data]);

  useEffect(() => {
    if (error) {
      setAlerts({
        message: `Error: ${error.message}`,
        severity: 'error',
      });
    }
  }, [error]);

  useEffect(() => {
    if (onToggleEdit) {
      onToggleEdit(activeEdit);
    }
  }, [activeEdit, onToggleEdit]);

  useEffect(() => {
    // If the user changes filters while in Edit Mode,
    // we're going to change their current active checklist
    // to a checklist that has filtered content
    if (activeEdit) {
      const agg = get(treed, '0.children', []).reduce((agg, item) => {
        get(item, 'children', []).forEach((cl) => {
          if (size(get(cl, 'children')) > 0) {
            if (getId(cl) === getId(get(activeEdit, 'checklist'))) {
              agg = false;
            } else if (agg === true) {
              agg = { checklist: cl, system: item };
            }
          }
        });
        return agg;
      }, true);

      if (get(agg, 'checklist')) {
        setAlerts({
          message: `No items in ${get(activeEdit, 'checklist.name', 'this category')}, switching to ${get(agg, 'checklist.name', 'another category')}`,
          severity: 'success',
        });
        setActiveEdit(agg);
      }
    }
  }, [treed]);

  useEffect(() => {
    props.onChange && props.onChange({ allData, treed });
  }, [allData]);

  useEffect(() => {
    const startingpage = props.page;
    return () => {
      //scores doesn't get updated when switching tabs because we update the treed data manually.
      // so need to get latest data when we leave the page. but only if we are still in this assessment
      if (window.location.pathname.includes(startingpage) && scores) {
        scores.update();
      }
    };
  }, []);

  //actions
  const update = async () => {
    if (get(scores, 'update')) {
      await scores.update();
    }
  };

  const setActiveEdit = useCallback((p) => {
    if (!get(p, 'checklist')) {
      return _setActiveEdit();
    }
    if (get(p, 'scroll')) {
      document.getElementById('allscores').scrollIntoView(true);
      window.scrollBy(0, get(p, 'scrollBy', 48));
    }
    _setActiveEdit({
      system: p.system,
      checklist: p.checklist,
    });
  }, []);

  const setSidebarOpen = useCallback((val) => {
    document.getElementsByTagName('body')[0].removeAttribute('style');
    setSidebar(val);
  }, []);

  let processing = useRef(false).current;
  const updateScores = useCallback(
    async (d, needsFullUpdate) => {
      if (processing) {
        await wait();
        return await updateScores(d, needsFullUpdate);
      }

      try {
        processing = true;
        if (needsFullUpdate) {
          if (!loading) {
            // setAlerts({
            //     message: `Error: Failed to update correctly. Updating tree to ensure we are up to date. Please wait.`,
            //     severity: 'error',
            // });
            await update();
          }
          return;
        } else {
          const item = get(d, '0', d);
          const checkpoint = get(item, 'processedScorecardScoreValue');
          const checklist = get(checkpoint, 'parent');
          const system = get(checklist, 'parent');
          const isGobs = isGob(checkpoint);

          assessment.update({ loading: false });

          let replaced = false;

          let newAll = scoredata.map((dd) => {
            if (getId(dd) == getId(checklist)) {
              console._log('found checklist', checklist, dd);
              dd = {
                ...dd,
                ...checklist,
                children: undefined,
                parent: undefined,
                id: dd.id,
                parentId: dd.parentId,
              };
              replaced = true;
            } else if (getId(dd) == getId(system)) {
              console._log('found system', system, dd);
              dd = {
                ...dd,
                ...system,
                id: dd.id,
                parentId: dd.parentId,
              };
              replaced = true;
            } else {
              get(checklist, 'children', []).map((cchild) => {
                if (getId(dd) == getId(cchild)) {
                  dd = {
                    ...dd,
                    ...cchild,
                    parent: undefined,
                    id: dd.id,
                    parentId: dd.parentId,
                    filters: {
                      ...dd.filters,
                      ...setFilters(cchild),
                    },
                  };
                  replaced = true;
                }
              });
            }
            return dd;
          });

          if (isGobs) {
            const found = newAll.find((na) => getId(na) === getId(checkpoint));
            if (!found) {
              newAll = [{ ...checkpoint, parent: undefined }, ...newAll];
            }
            replaced = true;
          }

          scoredata = newAll;

          if (!replaced) {
            return await update();
          }

          const { hasChildren } = await filterData(newAll, { ...filter, search: searchString, activeEdit: activeEdit, assessment: assessment });
          //need to do this because the state isn't necessarily getting updated before this method is called again, so the alldata is stale by that point
          treed = hasChildren;
          setTreed(hasChildren);
          allData[activeSc.id] = scoredata;
          setAllData({ ...allData });
        }
      } catch (err) {
        throw err;
      } finally {
        processing = false;
      }
    },
    [filter, searchString, activeEdit, assessment, activeSc, loading]
  );

  const isLocationView = get(filter, 'listGroup') === 'location';
  const isCategoryView = get(filter, 'listGroup') === 'category' || !isLocationView;

  useEffect(() => {
    setSelectedItems({});
  }, [filter.listGroup]);

  const itemPasses = useCallback(
    (item) => {
      if (isLocationView) {
        return true;
      } else if (isCategoryView && item.isSubScore) {
        return false;
      }

      return get(item, 'filters.act', true);
    },
    [isLocationView, isCategoryView]
  );

  const createKey = (item) => {
    const itemType = getItemType(item);
    return {
      ...item,
      itemType,
    };
  };

  const findParent = (it) => {
    const searchChildren = (children, id) => {
      const childs = children || [];
      let found = childs.find((c) => c.id == id);

      if (found) {
        return found;
      } else {
        for (let i = 0; i < childs.length; i++) {
          found = searchChildren(childs[i].children, id);
          if (found) {
            break;
          }
        }
      }
      return found;
    };

    return searchChildren(treed, it.parentId);
  };

  const selectItem = useCallback(
    (item) => {
      //#region check first
      if (item.value == null && !isLocationView) {
        return setAlerts({
          message: `Error: Not allowed to select an unscored item.`,
          severity: 'error',
        });
      }

      const itemType = getItemType(item);
      const passes = itemPasses(item);
      if (!passes) {
        return setAlerts({
          message: `Error: High scores and N/O scores must have notes or images attached in order to select.`,
          severity: 'error',
        });
      }
      //#endregion
      setShowSelectBoxes(true);

      let changed = 0;
      const allAdds = {};

      let selItems = { ...selectedItems };
      const itemKey = getItemKey(item);
      const exists = get(selItems, itemKey) || item.selectAll === false;

      const setAndAdd = (it, checklist, superset) => {
        [superset, checklist].forEach((obj) => {
          const key = getItemKey(obj);
          if (!get(allAdds, `${key}`)) {
            set(allAdds, `${key}.obj`, obj);
            set(allAdds, `${key}.max`, 0);
            set(allAdds, `${key}.count`, 0);
          }
          allAdds[key]['max'] += 1;
        });
        if (itemType === 'selectAll' || itemKey === getItemKey(superset) || itemKey === getItemKey(checklist) || itemKey === getItemKey(it)) {
          set(selItems, getItemKey(it), createKey(it));
          changed += 1;
        }
        if (get(selItems, getItemKey(it))) {
          allAdds[getItemKey(superset)]['count'] += 1;
          allAdds[getItemKey(checklist)]['count'] += 1;
        }
      };

      const updateParentItem = (it, changed, d = false) => {
        const keyId = getItemKey(it);
        if (d || get(selItems, `${keyId}.count`) === changed) {
          delete selItems[keyId];
        } else {
          set(selItems, `${keyId}.count`, get(selItems, `${keyId}.count`) - changed);
          set(selItems, `${keyId}.fullCount`, false);
        }
      };

      if (exists) {
        if (itemType === 'selectAll') {
          selItems = {};
        } else {
          const isSuperset = itemType === 'superset' || itemType === 'system';
          const isChecklist = itemType === 'checklist' || itemType === 'room';
          const supersetId = isSuperset ? `${itemType}_${getId(item)}` : `${isLocationView ? 'system' : 'superset'}_${get(item, 'parentId')}`;
          const checklistId = isChecklist ? `${itemType}_${getId(item)}` : `${isLocationView ? 'room' : 'checklist'}_${get(item, 'parentId')}`;
          get(treed, '0.children', []).some((supe) => {
            if (itemType === 'checkpoint' || getItemKey(supe) === supersetId) {
              const bool = (supe.children || []).some((child) => {
                if (isSuperset || getItemKey(child) === checklistId) {
                  (child.children || []).forEach((cchild) => {
                    // If this checkpoint passes,
                    // and if selected item is a checkpoint, must also be the selected checkpoint
                    if (itemPasses(cchild) && (itemType !== 'checkpoint' || getItemKey(cchild) === itemKey)) {
                      delete selItems[getItemKey(cchild)];
                      changed += 1;
                    }
                  });
                  updateParentItem(child, changed, itemType !== 'checkpoint');
                  if (!isSuperset) return true;
                }
              });
              updateParentItem(supe, changed, isSuperset);
              if (isSuperset) return true;
              return bool;
            }
          });
        }
      } else {
        get(treed, '0.children', []).forEach((supe) => {
          (supe.children || []).forEach((child) => {
            (child.children || []).forEach((cchild) => {
              if (itemPasses(cchild)) {
                setAndAdd(cchild, child, supe);
              }
            });
          });
        });
        Object.keys(allAdds).forEach((s) => {
          if (get(allAdds, `${s}.count`, 0) > 0) {
            set(
              selItems,
              s,
              createKey({
                ...get(allAdds, `${s}.obj`),
                fullCount: get(allAdds, `${s}.max`, 0) === get(allAdds, `${s}.count`, 0),
                count: get(allAdds, `${s}.count`, 0),
                max: get(allAdds, `${s}.max`, 0),
              })
            );
          }
        });
      }

      if (changed === 0 && !exists) {
        return setAlerts({
          message: `Nothing to select in this section.`,
          severity: 'error',
        });
      } else {
        if (changed > 0 && !exists) {
          setAlerts({
            message: `You added ${changed} new item${changed > 1 ? 's' : ''}.`,
          });
        }
        setSelectedItems(selItems);
      }
    },
    [selectedItems, treed]
  );

  const tv = get(treed, '0.children', []);

  const hasScore = (() => {
    const adata = get(assessment, 'data');
    const scores = get(adata, 'scores');
    const scardId = get(adata, ['scorecards', scorecardIndex, 'id']);
    return get(scores, [scardId, 'calculatedScore']);
  })();

  const createTasks = async (formState, items) => {
    const checkpoints = Object.keys(selectedItems)
      .filter((k) => get(selectedItems, [k, 'itemType']) === 'checkpoint')
      .map((cp) => get(selectedItems, cp));

    const order = []; //put the checkpoints in order so that the PDF renders them correctly
    const pushOrder = (check) => {
      if (size(check.subscores)) {
        check.subscores.map((ss) => order.push(ss));
      } else {
        order.push(check);
      }
    };
    (tv || []).map((sup) => {
      const childs = get(sup, 'children', []);
      childs.map((child) => {
        const check = checkpoints.find((cp) => getId(cp) == getId(child));
        if (check) {
          pushOrder(check);
        } else if (child.children) {
          child.children.map((_child) => {
            const _check = checkpoints.find((cp) => getId(cp) == getId(_child));
            if (_check) {
              pushOrder(_check);
            }
          });
        }
      });
    });

    await awaitAll(
      order.map((checkpoint) => () => async () => {
        const { isSubScore } = checkpoint;
        const checklist = findParent(checkpoint) || checkpoint.checklist;
        if (!isSubScore || (isSubScore && (get(checkpoint, 'notes') !== AUTOTAG && (!!get(checkpoint, 'notes') || !!size(get(checkpoint, 'images', [])))))) {

          const hasItem = get(items, get(checklist, 'checklistId', get(checkpoint, 'checklist.id')));

          let tradeTypes = [];
          if (hasItem) {
            tradeTypes = get(
              get(hasItem, 'checkpoints', []).find((c) => c.id == checkpoint.id),
              'tradeTypes',
              []
            );
          }

          const isGobs = isGob(checkpoint);

          const createdTask = await sendData(
            {
              url: `/tasks?include=images,assignees,viewers,authors,comments,comments.author,comments.images,realm,scorecardScore,scorecardScore.checklist,scorecardScore.checkpoint,scorecardScore.checkpoint.checkpointOptions,tradeTypes`,
              method: 'post',
              data: {
                data: {
                  type: 'tasks',
                  attributes: stringObj({
                    name: isGobs ? `${get(checklist, 'name', '')}: General Observations` : `${get(checklist, 'name', '')}: ${get(checkpoint, 'name')}`,
                    description: isGobs ? get(checkpoint, 'criteria') : get(checkpoint, 'notes'),
                    status: 0,
                    realm_id,
                    assessment_id,
                    scorecard_score_id: !isSubScore ? get(checkpoint, 'scorecardScoreId', '') : undefined,
                    sub_score_id: isSubScore ? checkpoint.id : undefined,
                    checkpoint_id: isGobs ? null : get(checkpoint, 'checkpointId', ''),
                  }),
                  relationships: {
                    authors: {
                      data: [stringObj({ type: 'users', role: 'author', id: me.id })],
                    },
                    assignees: {
                      data: get(formState, 'assignedTo', []).map((a) => stringObj({ type: 'users', role: 'assignee', id: a.id })),
                    },
                    tradeTypes: size(tradeTypes)
                      ? {
                          data: (tradeTypes || []).map((t, i) => ({ type: 'trade-types', id: t.id, pivot: { order: `${i}` } })),
                        }
                      : undefined,
                  },
                },
              },
            },
            { normalize: { build: ['tasks'] } }
          );

          const task = get(createdTask, '0');
          dispatch(addNewTask(task));
          return task;
        }
      }),
      { queueSize: 1 }
    );

    setSelectedItems({});
    assessmentTasks.update();
  };

  const onReview = () => {
    setFilter((f) => ({ ...f, listGroup: 'category' }));
  };

  if (window.SUPPORT) {
    window.UPDATE = update;
  }

  const subscoreProps = useMemo(
    () => ({
      scorecard: activeSc,
      rooms: {
        ...rooms,
        update: (e) => {
          rooms.update(e);
          get(window, 'MASTERREALM.update', () => 0)();
        },
        realmFilter,
      },
      allScoreData: scoredata,
      updateScores,
      assessment,
      setAlerts,
      activeEdit,
      hasSubScores,
      filter,
      onReview,
    }),
    [filter, activeEdit, assessment, scoredata, roomsLoading, activeSc]
  );

  if (!canShowScores) {
    if (assessmentLoading) {
      return (
        <GridContainer>
          <CenterContent fullWidth={printView} selfCenter>
            <div style={{ padding: 12 }}>
              <ScoreLoading />
            </div>
          </CenterContent>
        </GridContainer>
      );
    }
    return (
      <GridContainer>
        <CenterContent fullWidth={printView} selfCenter>
          <div style={{ padding: 12 }}>{`You don't have access to scores while this report is in ${upperFirst(status)}.`}</div>
        </CenterContent>
      </GridContainer>
    );
  }

  return (
    <>
      <NavToTop />
      <div className={clsx(classes.wrapper, isCourse ? classes.wrapperCourse : undefined)} style={{ flexDirection: printView ? 'column' : 'row' }}>
        {!printView && !isCourse && (
          <SideBar
            printView={printView}
            assessment={assessment}
            open={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            systems={tv}
            loading={loading}
            canShowScores={canShowScores}
            setActiveEdit={setActiveEdit}
            activeEdit={activeEdit}
            scorecardIndex={scorecardIndex}
          />
        )}
        <div id={'RightPanel'} className={clsx(classes.right, printView ? classes.rightPrint : undefined, isCourse ? classes.rightCourse : undefined)}>
          <FilterLogic
            {...{
              isCourse,
              isTemplate,
              assessment,
              activeSc,
              scorecardIndex,
              assessmentLoading,
              loading,
              status,
              updateScores: update,
              scoredata,
              setTreed,
              activeEdit,
              scorecards,
              setScorecardIndex,
              filter,
              setFilter,
              searchString,
              setSearchString,
              hasSubScores,
            }}
          />

          {!printView && !isCourse && (
            <>
              <TaskNotify realmId={realm_id || null} />
              <NavToTop />
            </>
          )}

          {userCanEdit && !activeEdit && !isTemplate && <ObservationCreateFab {...subscoreProps} />}

          <TaskBanner
            visible={!activeEdit && !isCourse && !printView}
            disabled={!hasScore}
            userCanEdit={userCanEdit}
            realm_id={realm_id || null}
            selectItem={selectItem}
            itemPasses={itemPasses}
            selectedItems={selectedItems}
            onCreateTasks={createTasks}
            allItems={tv}
            showSelectBoxes={showSelectBoxes}
            setShowSelectBoxes={setShowSelectBoxes}
            assessment={assessment.data}
            setSelectedItems={setSelectedItems}
          />

          <List id={'allscores'}>
            {!loading && !size(tv) && !!size(filter) && !printView && <div>There are no visible scores. Try changing the filters.</div>}
            {!activeEdit && loading && !!size(filter) && <ScoreLoading />}
            {!activeEdit &&
              !!size(filter) &&
              !loading &&
              tv.map((d, i) => {
                return (
                  <SystemScore
                    {...props}
                    key={d.id}
                    notFirst={i !== 0}
                    canShowScores={canShowScores}
                    userCanEdit={userCanEdit}
                    userCanModify={props.userCanModify}
                    assessment={assessment}
                    system={d}
                    assessmentTasks={assessmentTasks.data}
                    updateAssessmentTasks={get(assessmentTasks, 'update')}
                    systemIndex={i}
                    next={get(tv, i + 1)}
                    prev={get(tv, i - 1)}
                    setCurrentAnchor={setCurrentAnchor}
                    setSidebarOpen={setSidebarOpen}
                    setActiveEdit={setActiveEdit}
                    selectItem={selectItem}
                    selectedItems={selectedItems}
                    showSelectBoxes={showSelectBoxes}
                    filter={filter}
                    printView={printView}
                    scorecard={get(scorecards, scorecardIndex)}
                    userCanLink={userCanLink}
                    isCourse={isCourse}
                    updateScores={updateScores}
                    {...subscoreProps}
                  />
                );
              })}

            {activeEdit && size(tv) > 0 && (
              <ActiveEdit
                isCourse={isCourse}
                isTemplate={isTemplate}
                activeEdit={activeEdit}
                assessment={assessment}
                assessmentTasks={assessmentTasks}
                setActiveEdit={setActiveEdit}
                setSidebarOpen={setSidebarOpen}
                updateScores={updateScores}
                setCurrentAnchor={setCurrentAnchor}
                selectedItems={selectedItems}
                treed={tv}
                filter={filter}
                scorecard={get(scorecards, scorecardIndex)}
                {...subscoreProps}
              />
            )}
          </List>
          <div style={{ height: '3rem' }}></div>
        </div>

        <AssessmentSnackBar selectedItems={selectedItems} activeEdit={activeEdit} />
      </div>
    </>
  );
};

export default ReportAssessmentScoreContent;
